<script setup lang="ts">
import { reactive, ref, computed, watch, markRaw, PropType } from "vue";
import { useI18n } from "vue-i18n";
import {
    Department,
    DirectLink,
    Participant,
    Project,
    ProjectRole,
    StepNavItem,
    Trainer,
    TranslatedProduct,
} from "../../../types/PersologOnlineTypes";
import ConfigureProjectTab from "./ConfigureProjectTab.vue";
import ConfigureParticipantsTab from "./ConfigureParticipantsTab.vue";
import ConfigurePreviewTab from "./ConfigurePreviewTab.vue";
import ConfigureDirectLinkTab from "./ConfigureDirectLinkTab.vue";
import ConfigureRolesTab from "./ConfigureRolesTab.vue";
import ConfigureJobMatchingProject from "../job-match/ConfigureJobMatchingProject.vue";
import StepNavigation from "../../common/StepNavigation.vue";
import router from "../../../router";
import { autofocusFix } from "../../../utils/GeneralUtils";
import ConfigureDepartmentsTab from "./ConfigureDepartmentsTab.vue";
import {
    BENCHMARK_BASIC,
    BENCHMARK_JOBMATCH,
    productHasBenchmarkDiagram,
    productHasRoles,
    productNeedsDepartmentsTab,
} from "../../../utils/ProjectUtils";
import SuccessIcon from "../../common/SuccessIcon.vue";


const props = defineProps({
    product: {
        type: Object as PropType<TranslatedProduct>,
        default: null,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    products: {
        type: Array as PropType<TranslatedProduct[]>,
        default: () => [],
    },
    noLabel: {
        type: Boolean,
        default: false,
    },
    importProject: {
        type: Object as PropType<Project | null>,
        default: null,
    },
    importGroupChartId: {
        type: Number as PropType<number | null>,
        default: null,
    },
    inProjectOverview: {
        type: Boolean,
        default: false,
    },
});

const { t } = useI18n();

const defaultBenchmarkProject = BENCHMARK_JOBMATCH;

const emit = defineEmits(["update:project", "update:importProject", "end:importProject"]);

const menu = ref();

const dialogVisible = ref(false);
const addParticipantDialog = ref(false);
const directLinkDialog = ref(false);
const jobMatchDialogManualVisible = computed(() => props.importProject !== null);
const jobMatchDialogVisible = ref(false);
const jobMatchVisibleCombined = computed(() => jobMatchDialogVisible.value || jobMatchDialogManualVisible.value);

const project = ref<Project>();
const participants = ref<Participant[]>();
const departments = ref<Department[]>();
const selectedRoles = ref<ProjectRole[]>();
const focus = ref(props.importProject?.focus ?? "");
const selectedDepartments = ref<Department[]>();
const directLink = ref<DirectLink>();
const selectedProduct = ref<TranslatedProduct | undefined>(props.product);
const selectedProductManual = computed(
    () =>
        props.product ??
        (props.importProject === null
            ? undefined
            : props.products.find((p) => p.productNo === defaultBenchmarkProject)),
);
const selectedProductCombined = computed(() => selectedProduct.value ?? selectedProductManual.value);
const projectCreationSuccess = ref(false);
const createdProjectId = ref("");
const configureParticipantsTabRef = ref<InstanceType<typeof ConfigureParticipantsTab> | null>(null);

defineExpose({
    addParticipant: () => configureParticipantsTabRef.value?.addParticipant(),
});

const toggle = (event: Event) => menu.value.toggle(event);

const menuOptions = reactive([
    {
        label: computed(() => t("project-configuration.addParticipants")),
        icon: "pi pi-users",
        command: () => (addParticipantDialog.value = true),
        visible: computed(() => !productHasBenchmarkDiagram(selectedProductCombined.value?.productNo ?? "")),
    },
    {
        label: computed(() => t("project-configuration.directLink.create")),
        icon: "pi pi-link",
        command: () => (directLinkDialog.value = true),
        visible: computed(() => !productHasBenchmarkDiagram(selectedProductCombined.value?.productNo ?? "")),
    },
    {
        label: computed(() => t("project-configuration.jobMatch.createBenchmark")),
        icon: "pi pi-chart-bar",
        command: () => (jobMatchDialogVisible.value = true),
        visible: computed(
            () => productHasBenchmarkDiagram(selectedProductCombined.value?.productNo ?? "") || props.inProjectOverview,
        ),
    },
]);

function setProject(passedProject: Project) {
    project.value = passedProject;
    // project.value.focus = props.importProject?.focus ?? "";
    // if product has not been set as property, for example if a new project is created...
    if (!props.product) {
        selectedProduct.value = props.products?.find(
            (product: TranslatedProduct) => product.productNo === passedProject.product,
        );
    }
}

function setParticipants(passedParticipants: Participant[]) {
    participants.value = passedParticipants;
}

function setDirectLink(parsedDirectLink: DirectLink) {
    directLink.value = parsedDirectLink;
}

function setRoles(roles: ProjectRole[]) {
    selectedRoles.value = roles;
}

function setDepartments(departments: Department[]) {
    selectedDepartments.value = departments;
}

watch([addParticipantDialog, directLinkDialog], ([newVal1, newVal2]) => {
    dialogVisible.value = newVal1 || newVal2;
});

watch(
    () => props.product,
    (newProduct) => {
        selectedProduct.value = newProduct;
    },
);

function resetValues() {
    if (props.inProjectOverview) {
        project.value = undefined;
        selectedProduct.value = undefined;
        directLink.value = undefined;
        participants.value = [];
        selectedRoles.value = [];
    }
}

/**
 * Handles display of projects in the projects view after a project has been created
 * Generally handling an emitted event from the last step of the project configuration
 * @param createdProject the project that has been created
 */
function handleVisibilityUpdate(createdProject: Project) {
    addParticipantDialog.value = false;
    directLinkDialog.value = false;
    jobMatchDialogVisible.value = false;

    if (props.product) {
        projectCreationSuccess.value = true;
        createdProjectId.value = createdProject.id?.toString() ?? "";
    } else {
        projectCreationSuccess.value = true;
        // The overview reloads its first page on creation; a direct-link project's link rides along
        // on the reloaded surface, so the created project alone is enough here.
        emit("update:project", createdProject);
        // reset values for new dialog
        resetValues();
    }

    emit("update:importProject", props.importProject?.id ?? null);
}

// config of ParticipantDialog
const stepItemsParticipantDialog: StepNavItem[] = reactive([
    {
        label: computed(() => t("project-configuration.tabs.configureProject")),
        icon: "pi pi-wrench",
        disabled: false,
        component: markRaw(ConfigureProjectTab),
        props: {
            products: computed(() => props.products.filter((p) => p.productNo !== BENCHMARK_BASIC && p.productNo !== BENCHMARK_JOBMATCH)),
            product: selectedProduct,
            trainer: computed(() => props.trainer),
            project: project,
            departments: departments,
            focus: focus,
            productEditable: router.currentRoute.value.path == "/projects",
        },
        events: {
            project: setProject,
        },
    },
    {
        label: computed(() => t("project-configuration.tabs.addParticipants")),
        icon: "pi pi-users",
        disabled: false,
        ref: "configureParticipantsTabRef",
        component: markRaw(ConfigureParticipantsTab),
        props: {
            id: project.value?.id,
            participants: participants,
        },
        events: {
            "update:participants": setParticipants,
        },
    },
    {
        label: computed(() => t("project-configuration.tabs.preview")),
        icon: "pi pi-check",
        disabled: false,
        component: markRaw(ConfigurePreviewTab),
        props: {
            project: project,
            trainer: props.trainer,
            participants: participants,
            roles: selectedRoles,
            departments: selectedDepartments,
        },
        events: {
            "update:visible": handleVisibilityUpdate,
        },
    },
]);

// config of LinkDialog
const stepItemsDirectLinkDialog: StepNavItem[] = reactive([
    {
        label: computed(() => t("project-configuration.tabs.configureProject")),
        icon: "pi pi-wrench",
        disabled: false,
        component: markRaw(ConfigureProjectTab),
        props: {
            products: computed(() => props.products.filter((p) => p.productNo !== BENCHMARK_BASIC && p.productNo !== BENCHMARK_JOBMATCH)),
            product: selectedProduct,
            trainer: computed(() => props.trainer),
            project: project,
            departments: departments,
            productEditable: router.currentRoute.value.path == "/projects",
            hasDirectLink: directLinkDialog,
        },
        events: {
            project: setProject,
        },
    },
    {
        label: computed(() => t("project-configuration.tabs.configureDirectLink")),
        icon: "pi pi-envelope",
        disabled: false,
        component: markRaw(ConfigureDirectLinkTab),
        props: {
            project: project,
            product: selectedProduct,
            directLink: directLink,
            trainer: props.trainer,
        },
        events: {
            directLink: setDirectLink,
        },
    },
    {
        label: computed(() => t("project-configuration.tabs.preview")),
        icon: "pi pi-check",
        disabled: false,
        component: markRaw(ConfigurePreviewTab),
        props: {
            project: project,
            trainer: props.trainer,
            directLink: directLink,
            roles: selectedRoles,
            departments: selectedDepartments,
        },
        events: {
            "update:visible": handleVisibilityUpdate,
        },
    },
]);

/**
 * Adds the roles tab to the dialog if the product has roles
 */
function setRolesTab(hasRole: boolean) {
    cleanTab(ConfigureRolesTab, stepItemsParticipantDialog);
    cleanTab(ConfigureRolesTab, stepItemsDirectLinkDialog);
    if (hasRole) {
        stepItemsParticipantDialog.splice(1, 0, {
            label: t("project-configuration.tabs.selectRoles"),
            icon: "pi pi-users",
            disabled: false,
            component: markRaw(ConfigureRolesTab),
            props: {
                roles: selectedRoles,
                product: selectedProduct,
                projectLanguage: computed(() => project.value?.language ?? "de"),
            },
            events: {
                "update:roles": setRoles,
            },
        });
        stepItemsDirectLinkDialog.splice(1, 0, {
            label: t("project-configuration.tabs.selectRoles"),
            icon: "pi pi-users",
            disabled: false,
            component: markRaw(ConfigureRolesTab),
            props: {
                roles: selectedRoles,
                product: selectedProduct,
                projectLanguage: computed(() => project.value?.language ?? "de"),
            },
            events: {
                "update:roles": setRoles,
            },
        });
    }
}

/**
 * Adds the departments tab to the dialog if the product has departments
 */
function setDepartmentsTab(hasDepartments: boolean) {
    cleanTab(ConfigureDepartmentsTab, stepItemsParticipantDialog);
    cleanTab(ConfigureDepartmentsTab, stepItemsDirectLinkDialog);
    if (hasDepartments) {
        stepItemsParticipantDialog.splice(1, 0, {
            label: t("project-configuration.tabs.addDepartments"),
            icon: "pi pi-users",
            disabled: false,
            component: markRaw(ConfigureDepartmentsTab),
            props: {
                departments: departments,
                product: selectedProduct,
            },
            events: {
                "update:departments": setDepartments,
            },
        });
        stepItemsDirectLinkDialog.splice(1, 0, {
            label: t("project-configuration.tabs.addDepartments"),
            icon: "pi pi-users",
            disabled: false,
            component: markRaw(ConfigureDepartmentsTab),
            props: {
                departments: departments,
                product: selectedProduct,
            },
            events: {
                "update:departments": setDepartments,
            },
        });
    }
}

/**
 * Finds and removes the tab from the dialog. E.g. roles tab or departments tab
 * Returns true if the tab was found and removed.
 * @param componentTab either ConfigureRolesTab or ConfigureDepartmentsTab
 * @param configDialog either stepItemsParticipantDialog or stepItemsDirectLinkDialog
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cleanTab(componentTab: any, configDialog: StepNavItem[]) {
    const tabIndex = configDialog.findIndex((tab) => tab.component == markRaw(componentTab));
    if (tabIndex !== -1) {
        configDialog.splice(tabIndex, 1);
        return true;
    }
    return false;
}

/**
 * True while it is still unknown whether the selected product has a roles step. That answer needs a
 * request, so "continue" stays disabled until it is in: clicked earlier, the wizard moved on from the
 * three-step list and skipped the roles step altogether.
 */
const productStepsPending = ref(false);
let productStepsRequest = 0;

/**
 * Adds the product-specific steps (departments, roles) for the selected product. Decided here and not
 * in the first step, because that step is unmounted by the time a slow roles lookup answers, and Vue
 * drops events from unmounted components.
 */
async function setProductSteps(productNo: string | undefined) {
    const request = ++productStepsRequest;
    setDepartmentsTab(productNeedsDepartmentsTab(productNo ?? ""));
    if (!productNo) {
        setRolesTab(false);
        productStepsPending.value = false;
        return;
    }

    productStepsPending.value = true;
    let hasRoles = false;
    try {
        hasRoles = await productHasRoles(productNo);
    } catch (error) {
        console.error(`Error checking whether product ${productNo} has roles:`, error);
    }
    // a newer product selection owns the steps now
    if (request !== productStepsRequest) return;
    setRolesTab(hasRoles);
    productStepsPending.value = false;
}

watch(() => selectedProduct.value?.productNo, setProductSteps, { immediate: true });

/**
 * Watches for the projectCreationSuccess flag and navigates to the projects view with the new project id
 */
watch(projectCreationSuccess, (newVal) => {
    if (!newVal && createdProjectId.value) {
        // jump to projects view with the new project id
        router.push({ path: "/projects", query: { projectId: createdProjectId.value } });
    }
});

const headlineNewProject = computed(() => {
    if (props.importProject) {
        return t("project-configuration.jobMatch.createBenchmark");
    } else if (props.product) {
        return t("project-configuration.tabs.newProject") + " - " + props.product.productName;
    } else if (addParticipantDialog.value) {
        return t("project-configuration.tabs.newProject") + " - " + t("project-configuration.tabs.addParticipants");
    } else if (directLinkDialog.value) {
        return t("project-configuration.tabs.newProject") + " - " + t("project-configuration.tabs.configureDirectLink");
    } else {
        return t("project-configuration.tabs.newProject");
    }
});

// visual config of dialogs
const dialogPassThrough = {
    root: { style: { width: "60vw" } },
    content: { style: { flex: 1 } },
};

const dialogPassThroughBenchmark = {
    root: { style: { width: "90vw" } },
    content: { style: { flex: 1 } },
};
const dialogBreakpoints = { "1199px": "85vw", "1000px": "95vw" };
</script>

<template>
    <SuccessIcon
        :success-message="t('project-configuration.tabs.success')"
        :visible="projectCreationSuccess"
        @update:visible="projectCreationSuccess = false"
    />
    <Button
        v-if="noLabel"
        :id="'configure-project_btn_use-' + selectedProduct?.productNo"
        @click="toggle"
        aria-haspopup="true"
        icon="pi pi-plus"
        aria-controls="overlay_menu"
        v-tooltip.top="{ value: t('project-overview.tooltips.addNew') }"
        @focus="autofocusFix"
    />
    <Button
        v-if="productHasBenchmarkDiagram(product?.productNo ?? '')"
        :id="'configure-project_btn_use-' + product?.productNo"
        @click="() => (jobMatchDialogVisible = true)"
        aria-haspopup="true"
        icon="pi pi-chart-bar"
        :label="t('project-configuration.jobMatch.createBenchmark')"
    />
    <Button
        v-if="!noLabel && !productHasBenchmarkDiagram(product?.productNo ?? '')"
        :id="'configure-project_btn_use-' + product?.productNo"
        @click="toggle"
        aria-haspopup="true"
        icon="pi pi-id-card"
        aria-controls="overlay_menu"
        :label="t('project-configuration.useButton')"
        @focus="autofocusFix"
    />
    <Menu
        ref="menu"
        :id="'configure-project_btn_' + product?.productNo"
        :model="menuOptions"
        :popup="true"
        @focus="autofocusFix"
    />

    <Dialog
        v-model:visible="addParticipantDialog"
        modal
        :header="headlineNewProject"
        :breakpoints="dialogBreakpoints"
        :pt="dialogPassThrough"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        @update:visible="resetValues"
    >
        <StepNavigation
            :steps="stepItemsParticipantDialog"
            :back-label="t('buttons.back')"
            :next-label="t('buttons.next')"
            :final-label="t('buttons.save')"
            :next-disabled="productStepsPending"
        />
    </Dialog>

    <Dialog
        v-model:visible="directLinkDialog"
        modal
        :header="headlineNewProject"
        :breakpoints="dialogBreakpoints"
        :pt="dialogPassThrough"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        @update:visible="resetValues"
    >
        <StepNavigation
            :steps="stepItemsDirectLinkDialog"
            :back-label="t('buttons.back')"
            :next-label="t('buttons.next')"
            :final-label="t('buttons.save')"
            :next-disabled="productStepsPending"
        />
    </Dialog>

    <!-- Job Matching Project Creation -->
    <Dialog
        v-model:visible="jobMatchVisibleCombined"
        modal
        :header="headlineNewProject"
        :breakpoints="dialogBreakpoints"
        :pt="dialogPassThroughBenchmark"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        @update:visible="
            emit('end:importProject', importProject?.id ?? null);
            jobMatchDialogVisible = false;
            resetValues();
        "
    >
        <ConfigureJobMatchingProject
            :product="selectedProductCombined ?? props.products.find((p) => p.productNo === defaultBenchmarkProject)"
            :products="products.filter((p) => p.productNo === defaultBenchmarkProject)"
            :trainer="trainer"
            :project="project"
            :import-project="importProject"
            :import-group-chart-id="importGroupChartId"
            @set:project="setProject"
            @update:visible="handleVisibilityUpdate"
        />
    </Dialog>
</template>
